import { useEffect, useState } from 'react';
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton,
    Tab, Tabs, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MDButton from '@components/MDButton';
import { StandaloneAutoCompleteSelect } from '@components/StandaloneAutoCompleteSelect';
import { largeDialog } from 'assets/theme/dialogSize';
import { setLoading, useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import {
    LeadFormsService, MetaQuestionType, NewMetaQuestion,
} from '../services/lead-forms.service';

interface Props {
    open: boolean;
    pageId: string;
    pageName: string;
    onClose: () => void;
    /// Devolve o formulário recém-criado para o assistente seguir com ele.
    onCreated: (form: { id: string; name: string }) => void;
}

/// Os três primeiros o CRM reconhece (nome, telefone, e-mail); o resto cai no histórico.
const QUESTION_TYPES: { value: MetaQuestionType; recognized: boolean }[] = [
    { value: 'FULL_NAME', recognized: true },
    { value: 'EMAIL', recognized: true },
    { value: 'PHONE', recognized: true },
    { value: 'FIRST_NAME', recognized: false },
    { value: 'LAST_NAME', recognized: false },
    { value: 'COMPANY_NAME', recognized: false },
    { value: 'JOB_TITLE', recognized: false },
    { value: 'CITY', recognized: false },
    { value: 'STATE', recognized: false },
    { value: 'STREET_ADDRESS', recognized: false },
    { value: 'ZIP', recognized: false },
    { value: 'CUSTOM', recognized: false },
];

const DEFAULTS: NewMetaQuestion[] = [
    { type: 'FULL_NAME' },
    { type: 'PHONE' },
    { type: 'EMAIL' },
];

export const LeadFormBuilder = ({ open, pageId, pageName, onClose, onCreated }: Props) => {
    const { t } = useTranslation();
    const [controller, dispatch] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();

    const [name, setName] = useState('');
    const [questions, setQuestions] = useState<NewMetaQuestion[]>(DEFAULTS);
    const [privacyUrl, setPrivacyUrl] = useState('');
    const [contextTitle, setContextTitle] = useState('');
    const [contextItemsText, setContextItems] = useState('');
    const [thankYouTitle, setThankYouTitle] = useState('');
    const [thanksText, setThanksText] = useState('');
    const [thankYouButton, setThankYouButton] = useState('');

    /// Qual tela da prévia está aberta. O contato passa pelas três em ordem.
    const [previewScreen, setPreviewScreen] = useState(1);

    useEffect(() => {
        if (!open) return;

        setName('');
        setQuestions(DEFAULTS);
        setPrivacyUrl('');
        setContextTitle('');
        setContextItems('');
        setThankYouTitle('');
        setThanksText('');
        setThankYouButton('');
        setPreviewScreen(1);
    }, [open]);

    const changeType = (index: number, nextType: MetaQuestionType) => {
        setQuestions((current) => current.map((question, i) =>
            i === index
                // Trocar de CUSTOM para um tipo da Meta descarta o texto, só existente na personalizada.
                ? { type: nextType, label: nextType === 'CUSTOM' ? question.label ?? '' : undefined }
                : question));
    };

    const changeText = (index: number, text: string) => {
        setQuestions((current) => current.map((question, i) =>
            i === index ? { ...question, label: text } : question));
    };

    const remove = (index: number) =>
        setQuestions((current) => current.filter((_, i) => i !== index));

    const add = () =>
        setQuestions((current) => [...current, { type: 'CUSTOM', label: '' }]);

    const contextItems = contextItemsText
        .split('\n')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);

    const save = async () => {
        if (name.trim().length === 0) {
            showAlert(t('leadForms.builder.nomeObrigatorio'), 'warning', 4000);
            return;
        }

        if (questions.length === 0) {
            showAlert(t('leadForms.builder.semPerguntas'), 'warning', 4000);
            return;
        }

        const emptyCustom = questions.some(
            (question) => question.type === 'CUSTOM' && (question.label ?? '').trim().length === 0);

        if (emptyCustom) {
            showAlert(t('leadForms.builder.textoObrigatorio'), 'warning', 4000);
            return;
        }

        if (privacyUrl.trim().length === 0) {
            showAlert(t('leadForms.builder.politicaObrigatoria'), 'warning', 5000);
            return;
        }

        setLoading(dispatch, true);

        try {
            const res = await new LeadFormsService().createForm(pageId, {
                name: name.trim(),
                questions,
                privacyPolicyUrl: privacyUrl.trim(),
                contextTitle: contextTitle.trim() || undefined,
                contextItems,
                thankYouTitle: thankYouTitle.trim() || undefined,
                thankYouBody: thanksText.trim() || undefined,
                thankYouButtonText: thankYouButton.trim() || undefined,
            });

            onCreated(res.data.data);
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 6000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    const section = (text: string, required = false) => (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.8, mb: 1 }}>
            <Typography
                variant="caption"
                sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}
            >
                {text}
            </Typography>
            <Typography
                variant="caption"
                sx={{
                    fontSize: '10px',
                    color: required ? cc.eventDanger : cc.textMuted,
                    fontWeight: required ? 700 : 400,
                }}
            >
                {required
                    ? t('leadForms.builder.obrigatorio')
                    : t('leadForms.builder.opcional')}
            </Typography>
        </Box>
    );

    /// Como o contato vê cada pergunta: tipo da Meta vira rótulo em português; personalizada, o texto.
    const questionLabel = (question: NewMetaQuestion) =>
        question.type === 'CUSTOM'
            ? (question.label ?? '').trim() || t('leadForms.builder.preview.perguntaSemTexto')
            : t(`leadForms.builder.tipos.${question.type}`);

    const mobileLine = (content: React.ReactNode) => (
        <Box
            sx={{
                width: '100%',
                maxWidth: 330,
                border: `1px solid ${cc.borderStrong}`,
                borderRadius: '22px',
                bgcolor: cc.cardBg,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* A Meta mostra a página como remetente no topo do formulário. */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: '12px 14px',
                    borderBottom: `1px solid ${cc.divider}`,
                }}
            >
                <Box
                    sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        bgcolor: cc.countBg,
                        color: cc.textSecondary,
                        fontSize: 11,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {pageName.trim().charAt(0).toUpperCase()}
                </Box>
                <Typography
                    variant="caption"
                    noWrap
                    sx={{ fontWeight: 700, color: cc.textPrimary }}
                >
                    {pageName}
                </Typography>
            </Box>

            <Box sx={{ p: '16px 14px 18px' }}>{content}</Box>
        </Box>
    );

    const emptyPreview = (text: string) => (
        <Box sx={{ py: 5, textAlign: 'center' }}>
            <InfoOutlinedIcon sx={{ fontSize: 26, color: cc.textMuted, mb: 0.8 }} />
            <Typography
                variant="caption"
                sx={{ display: 'block', color: cc.textSecondary, lineHeight: 1.5 }}
            >
                {text}
            </Typography>
        </Box>
    );

    const fakeButton = (text: string) => (
        <Box
            sx={{
                mt: 2,
                bgcolor: cc.accent,
                color: '#fff',
                textAlign: 'center',
                borderRadius: '999px',
                py: 1,
                fontSize: 13,
                fontWeight: 700,
            }}
        >
            {text}
        </Box>
    );

    const introPreview = () => (contextTitle.trim().length === 0
        ? emptyPreview(t('leadForms.builder.preview.semApresentacao'))
        : (
            <>
                <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, fontSize: 17, lineHeight: 1.3, color: cc.textPrimary }}
                >
                    {contextTitle}
                </Typography>

                {contextItems.length > 0 && (
                    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {contextItems.map((item, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 5,
                                        height: 5,
                                        borderRadius: '50%',
                                        bgcolor: cc.accent,
                                        mt: '7px',
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{ color: cc.textSecondary, lineHeight: 1.5 }}
                                >
                                    {item}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                {fakeButton(t('leadForms.builder.preview.continuar'))}
            </>
        ));

    const questionsPreview = () => (
        <>
            <Typography
                variant="body2"
                sx={{ fontWeight: 700, fontSize: 15, mb: 1.5, color: cc.textPrimary }}
            >
                {t('leadForms.builder.preview.cabecalho')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                {questions.map((question, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Box key={index}>
                        <Typography
                            variant="caption"
                            sx={{ display: 'block', color: cc.textMuted, fontSize: '10.5px', mb: 0.3 }}
                        >
                            {questionLabel(question)}
                        </Typography>
                        <Box
                            sx={{
                                height: 30,
                                borderRadius: '6px',
                                border: `1px solid ${cc.border}`,
                                bgcolor: cc.countBg,
                            }}
                        />
                    </Box>
                ))}
            </Box>

            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    mt: 2,
                    fontSize: '10.5px',
                    color: privacyUrl.trim().length > 0 ? cc.accent : cc.eventDanger,
                }}
            >
                {privacyUrl.trim().length > 0
                    ? t('leadForms.builder.preview.politicaLink')
                    : t('leadForms.builder.preview.politicaFaltando')}
            </Typography>

            {fakeButton(t('leadForms.builder.preview.enviar'))}
        </>
    );

    const thanksPreview = () => (thankYouTitle.trim().length === 0
        ? emptyPreview(t('leadForms.builder.preview.semAgradecimento'))
        : (
            <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, fontSize: 16, color: cc.textPrimary }}
                >
                    {thankYouTitle}
                </Typography>
                {thanksText.trim().length > 0 && (
                    <Typography
                        variant="caption"
                        sx={{ display: 'block', mt: 1, color: cc.textSecondary, lineHeight: 1.5 }}
                    >
                        {thanksText}
                    </Typography>
                )}
                {/* Sem texto não há botão: é o que vai para a Meta, e mostrar um seria mentira. */}
                {thankYouButton.trim().length > 0
                    && fakeButton(thankYouButton.trim())}
            </Box>
        ));

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" scroll="paper">
            <DialogTitle>{t('leadForms.builder.titulo', { page: pageName })}</DialogTitle>

            <DialogContent dividers style={largeDialog}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 380px' },
                        columnGap: 4,
                        rowGap: 3,
                        alignItems: 'start',
                    }}
                >
                    <Box>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('leadForms.builder.nome')}
                            helperText={t('leadForms.builder.nomeAjuda')}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            sx={{ mb: 2.5 }}
                        />

                        {section(t('leadForms.builder.perguntas'), true)}
                        <Typography
                            variant="caption"
                            sx={{ display: 'block', color: cc.textSecondary, mb: 1.5, lineHeight: 1.5 }}
                        >
                            {t('leadForms.builder.perguntasAjuda')}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                            {questions.map((question, index) => (
                                // O índice é a identidade: nenhuma pergunta tem id antes de existir na Meta.
                                // eslint-disable-next-line react/no-array-index-key
                                <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                                    <Box
                                        sx={{
                                            width: question.type === 'CUSTOM' ? 230 : '100%',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <StandaloneAutoCompleteSelect
                                            options={QUESTION_TYPES.map((nextType) => ({
                                                value: nextType.value,
                                                label: `${t(`leadForms.builder.tipos.${nextType.value}`)}${
                                                    nextType.recognized ? ' ★' : ''}`,
                                            }))}
                                            value={question.type}
                                            onChange={(value) =>
                                                changeType(index, value as MetaQuestionType)}
                                        />
                                    </Box>

                                    {question.type === 'CUSTOM' && (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder={t('leadForms.builder.texto')}
                                            value={question.label ?? ''}
                                            onChange={(e) => changeText(index, e.target.value)}
                                        />
                                    )}

                                    <IconButton
                                        onClick={() => remove(index)}
                                        aria-label={t('leadForms.removerPergunta')}
                                        disabled={questions.length === 1}
                                        sx={{ flexShrink: 0 }}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>

                        <MDButton
                            variant="text"
                            color="info"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={add}
                            sx={{ mt: 1 }}
                        >
                            {t('leadForms.builder.adicionarPergunta')}
                        </MDButton>

                        <Divider sx={{ my: 2.5 }} />

                        {section(t('leadForms.builder.politica'), true)}
                        <TextField
                            fullWidth
                            size="small"
                            label={t('leadForms.builder.politicaUrl')}
                            placeholder="https://"
                            helperText={t('leadForms.builder.politicaAjuda')}
                            value={privacyUrl}
                            onChange={(e) => setPrivacyUrl(e.target.value)}
                        />

                        <Divider sx={{ my: 2.5 }} />

                        {/* Opcionais recuadas: quem quer só o essencial não precisa ler daqui para baixo. */}
                        <Box sx={{ borderLeft: `2px solid ${cc.divider}`, pl: 2 }}>
                            {section(t('leadForms.builder.apresentacao'))}
                            <TextField
                                fullWidth
                                size="small"
                                label={t('leadForms.builder.contextoTitulo')}
                                value={contextTitle}
                                onChange={(e) => setContextTitle(e.target.value)}
                                onFocus={() => setPreviewScreen(0)}
                                sx={{ mb: 1.5 }}
                            />
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                size="small"
                                label={t('leadForms.builder.contextoItens')}
                                helperText={t('leadForms.builder.contextoItensAjuda')}
                                value={contextItemsText}
                                onChange={(e) => setContextItems(e.target.value)}
                                onFocus={() => setPreviewScreen(0)}
                            />

                            <Box sx={{ height: 20 }} />

                            {section(t('leadForms.builder.agradecimento'))}
                            <TextField
                                fullWidth
                                size="small"
                                label={t('leadForms.builder.agradecimentoTitulo')}
                                value={thankYouTitle}
                                onChange={(e) => setThankYouTitle(e.target.value)}
                                onFocus={() => setPreviewScreen(2)}
                                sx={{ mb: 1.5 }}
                            />
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                size="small"
                                label={t('leadForms.builder.agradecimentoTexto')}
                                value={thanksText}
                                onChange={(e) => setThanksText(e.target.value)}
                                onFocus={() => setPreviewScreen(2)}
                                sx={{ mb: 1.5 }}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label={t('leadForms.builder.agradecimentoBotao')}
                                helperText={t('leadForms.builder.agradecimentoBotaoAjuda')}
                                value={thankYouButton}
                                onChange={(e) => setThankYouButton(e.target.value)}
                                onFocus={() => setPreviewScreen(2)}
                            />
                        </Box>
                    </Box>

                    <Box sx={{ position: { md: 'sticky' }, top: 0 }}>
                        {section(t('leadForms.builder.preview.titulo'))}

                        <Tabs
                            value={previewScreen}
                            onChange={(_, value) => setPreviewScreen(value)}
                            variant="fullWidth"
                            sx={{ mb: 2 }}
                        >
                            <Tab label={t('leadForms.builder.preview.abaApresentacao')} />
                            <Tab label={t('leadForms.builder.preview.abaPerguntas')} />
                            <Tab label={t('leadForms.builder.preview.abaAgradecimento')} />
                        </Tabs>

                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            {mobileLine(
                                previewScreen === 0
                                    ? introPreview()
                                    : previewScreen === 2
                                        ? thanksPreview()
                                        : questionsPreview()
                            )}
                        </Box>

                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                mt: 1.5,
                                textAlign: 'center',
                                color: cc.textMuted,
                                fontSize: '10.5px',
                                lineHeight: 1.5,
                            }}
                        >
                            {t('leadForms.builder.preview.aviso')}
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'space-between', gap: 2 }}>
                {/* Aqui e não no topo: a imutabilidade importa ao apertar o botão, não ao preencher. */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: 560 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: cc.textMuted, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: cc.textSecondary, lineHeight: 1.4 }}>
                        {t('leadForms.builder.imutavel')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <MDButton variant="gradient" color="dark" onClick={onClose}>
                        {t('general.cancelar')}
                    </MDButton>
                    <MDButton variant="gradient" color="success" onClick={save}>
                        {t('leadForms.builder.criar')}
                    </MDButton>
                </Box>
            </DialogActions>
        </Dialog>
    );
};
